import {
  MutationOptions,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { AUTO_HIDE_DURATION_MS } from "../constants";
import { ContactInfo, RfpCarriersResponse } from "../types/carrier";
import { extractErrorMessage } from "../utils";

import { getRfpFinalDecisionCarriersQueryKey } from "./useRfpFinalDecisionCarriers";
import useToastNotificationService from "./useToastNotificationService";

type UpdateContactInfo = Pick<ContactInfo, "type" | "email">;

interface RfpCarrierContact {
  carrierName: string;
  contactInfo: UpdateContactInfo[];
}

interface UpdateRfpCarriersPayload {
  carriers: RfpCarrierContact[];
}

interface UseUpdateRfpCarriersOptions extends Pick<
  MutationOptions<RfpCarriersResponse, Error, UpdateRfpCarriersPayload>,
  "onSuccess" | "onError"
> {
  rfpId: string;
  successMessage?: string;
  errorMessage?: string;
}

function useUpdateRfpCarriers({
  rfpId,
  successMessage,
  errorMessage,
  onError,
  onSuccess,
}: UseUpdateRfpCarriersOptions): UseMutationResult<
  RfpCarriersResponse,
  Error,
  UpdateRfpCarriersPayload
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();

  return useMutation<RfpCarriersResponse, Error, UpdateRfpCarriersPayload>({
    mutationFn: (body) =>
      axiosInstance
        .patch<RfpCarriersResponse>(
          `brokerportal/v1/rfp/${rfpId}/carriers`,
          body,
        )
        .then((res) => res.data),
    onSuccess: (...params) => {
      queryClient.invalidateQueries({
        queryKey: getRfpFinalDecisionCarriersQueryKey(rfpId),
      });
      onSuccess?.(...params);
      if (successMessage) {
        createToastNotification({
          message: successMessage,
          severity: "success",
          autoHideDuration: AUTO_HIDE_DURATION_MS,
        });
      }
    },
    onError: (error, ...errorParams) => {
      onError?.(error, ...errorParams);
      createToastNotification({
        message: errorMessage ?? extractErrorMessage(error),
        severity: "error",
        autoHideDuration: AUTO_HIDE_DURATION_MS,
      });
    },
  });
}

export { useUpdateRfpCarriers };
